import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Clock, Calendar, User, Award, ArrowLeft, ExternalLink, Clapperboard } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FilmDetail() {
  const { id } = useParams();
  const [film, setFilm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFilm = async () => {
      try {
        const response = await axios.get(`${API}/films/${id}`);
        setFilm(response.data);
      } catch (error) {
        console.error('Error fetching film:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFilm();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-neutral-950 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!film) {
    return (
      <div className="min-h-screen pt-20 bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl text-white mb-4">FILM NOT FOUND</h1>
          <Link to="/films" className="text-orange-500 hover:text-orange-400">
            Back to Films
          </Link>
        </div>
      </div>
    );
  }

  // Determine if film is coming soon
  const isComingSoon = film.status === 'in_production' || film.year > new Date().getFullYear() || (film.platforms && film.platforms.length === 0);
  const releaseText = film.release_date || `${film.year}`;

  return (
    <div data-testid="film-detail-page" className="min-h-screen pt-20 bg-neutral-950">
      {/* Hero */}
      <section className="relative">
        {/* Background */}
        <div className="absolute inset-0 h-[70vh]">
          <img
            src={film.header_url || film.poster_url}
            alt={film.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/80 to-neutral-950/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950 via-neutral-950/50 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-24">
          {/* Back Button */}
          <Link
            to="/films"
            data-testid="back-to-films"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            Back to Films
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            {/* Poster */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="hidden lg:block"
            >
              <img
                src={film.poster_url}
                alt={film.title}
                className="w-full aspect-[2/3] object-cover rounded-sm shadow-2xl"
              />
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2"
            >
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                {isComingSoon ? (
                  <span className="inline-block bg-orange-600 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider animate-pulse">
                    COMING {releaseText}
                  </span>
                ) : (
                  <span className="inline-block bg-orange-600/20 border border-orange-500/30 text-orange-400 text-xs font-bold px-3 py-1 uppercase tracking-wider">
                    NOW AVAILABLE
                  </span>
                )}
                <span className="inline-block bg-white/10 border border-white/20 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
                  {film.genre}
                </span>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight uppercase leading-none text-white mb-4">
                {film.title}
              </h1>

              {/* Tagline */}
              {film.tagline && (
                <p className="text-orange-500 text-xl font-medium italic mb-6">
                  {film.tagline}
                </p>
              )}

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-6 text-neutral-400 mb-8">
                <div className="flex items-center gap-2">
                  <Calendar size={18} className="text-orange-500" />
                  <span>{isComingSoon ? releaseText : film.year}</span>
                </div>
                {film.runtime && film.runtime !== 'TBC' && (
                  <div className="flex items-center gap-2">
                    <Clock size={18} className="text-orange-500" />
                    <span>{film.runtime}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <User size={18} className="text-orange-500" />
                  <span>Dir. {film.director}</span>
                </div>
              </div>

              {/* Coming Soon Status */}
              {isComingSoon && (
                <div className="flex items-center gap-3 p-4 bg-white/5 border border-white/10 mb-8">
                  <Clapperboard className="text-orange-500" size={24} />
                  <div>
                    <span className="text-white font-medium block">Coming {releaseText}</span>
                    <span className="text-neutral-400 text-sm">This film is currently in production</span>
                  </div>
                </div>
              )}

              {/* Synopsis */}
              <p className="text-neutral-300 text-lg leading-relaxed mb-8 max-w-2xl">
                {film.synopsis}
              </p>

              {/* Awards */}
              {film.awards && film.awards.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-display text-lg tracking-wider text-white uppercase mb-4">AWARDS & RECOGNITION</h3>
                  <div className="flex flex-wrap gap-3">
                    {film.awards.map((award, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 bg-neutral-900 border border-white/10 px-4 py-2"
                      >
                        <Award size={16} className="text-orange-500" />
                        <span className="text-sm text-neutral-300">{award}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Watch On - Only show if platforms available */}
              {!isComingSoon && film.platforms && film.platforms.length > 0 && (
                <div>
                  <h3 className="font-display text-lg tracking-wider text-white uppercase mb-4">WATCH ON</h3>
                  <div className="flex flex-wrap gap-4">
                    {film.platforms.map((platform) => (
                      <a
                        key={platform}
                        href="#"
                        data-testid={`platform-link-${platform.toLowerCase().replace(' ', '-')}`}
                        className="flex items-center gap-2 bg-neutral-900 border border-white/10 hover:border-orange-500 px-6 py-3 transition-colors group"
                      >
                        <span className="text-white font-medium">{platform}</span>
                        <ExternalLink size={16} className="text-neutral-500 group-hover:text-orange-500 transition-colors" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Trailer Button - Only show if trailer available */}
              {film.trailer_url && (
                <a
                  href={film.trailer_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="play-trailer-btn"
                  className="mt-10 inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-8 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
                >
                  <Play size={20} fill="white" />
                  WATCH TRAILER
                </a>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 bg-neutral-900/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="font-display text-2xl tracking-wider text-white uppercase mb-6">ABOUT THE FILM</h3>
              <p className="text-neutral-400 leading-relaxed">
                {film.synopsis}
              </p>
              {film.full_synopsis && (
                <p className="text-neutral-400 leading-relaxed mt-4">
                  {film.full_synopsis}
                </p>
              )}
              {isComingSoon ? (
                <p className="text-neutral-400 leading-relaxed mt-4">
                  This {film.genre.toLowerCase()} is coming {releaseText}. Stay tuned for more updates on distribution and release.
                </p>
              ) : (
                <p className="text-neutral-400 leading-relaxed mt-4">
                  Distributed by LBR Distribution, this {film.genre.toLowerCase()} is now available on multiple streaming platforms worldwide. Experience the vision of director {film.director} from the comfort of your home.
                </p>
              )}
            </div>
            <div>
              <h3 className="font-display text-2xl tracking-wider text-white uppercase mb-6">
                {isComingSoon ? 'PRODUCTION INFO' : 'DISTRIBUTION INFO'}
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-neutral-500">Distributor</span>
                  <span className="text-white">LBR Distribution</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-neutral-500">{isComingSoon ? 'Expected Release' : 'Release Year'}</span>
                  <span className="text-white">{releaseText}</span>
                </div>
                {film.runtime && film.runtime !== 'TBC' && (
                  <div className="flex justify-between items-center py-3 border-b border-white/10">
                    <span className="text-neutral-500">Runtime</span>
                    <span className="text-white">{film.runtime}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-3 border-b border-white/10">
                  <span className="text-neutral-500">Genre</span>
                  <span className="text-white">{film.genre}</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-neutral-500">Status</span>
                  <span className={isComingSoon ? "text-orange-500 font-medium" : "text-white"}>
                    {isComingSoon ? 'In Production' : 'Available Now'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
